import React from 'react';
import { useTranslation } from 'react-i18next';
import { useChartConfig } from './hooks/useChartConfig';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ChartContainer } from './shared/ChartContainer';
import { ChartContent } from './shared/ChartContent';
import { ChartFooter } from './shared/ChartFooter';

export interface AppVersionStat {
  appVersion: string;
  numberOfUsers: number;
}

interface Props {
  data: AppVersionStat[];
}

export const AppVersionStatsChart: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const config = useChartConfig();

  return (
    <ChartContainer>
      <ChartContent>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart
            data={data}
            margin={{ top: 25, right: 30, left: 0, bottom: 60 }}
            barSize={20}
            style={config.styles.noOutline}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={config.colors.grid} />
            <XAxis
              dataKey="appVersion"
              scale="point"
              padding={{ left: 40, right: 40 }}
              {...config.axis}
              label={{
                value: t('dashboard.charts.appVersion'),
                position: 'bottom',
                offset: 30,
              }}
            />
            <YAxis
              allowDecimals={false}
              label={{
                value: t('dashboard.charts.numberOfUsers'),
                angle: -90,
                position: 'insideLeft',
              }}
              {...config.axis}
            />
            <Tooltip
              {...config.tooltip}
              formatter={(value: number) =>
                `${value} ${t('dashboard.charts.users')}`
              }
              labelFormatter={(label: string) =>
                `${t('dashboard.charts.appVersion')}: ${label}`
              }
            />
            <Legend {...config.legend} />
            <Bar
              dataKey="numberOfUsers"
              name={t('dashboard.charts.users')}
              fill={config.colors.primary}
              background={{ fill: config.colors.barBackground }}
              radius={[6, 6, 0, 0]}
              activeBar={{ fill: config.colors.primary, opacity: 0.6 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContent>
      {/* Footer */}
      <ChartFooter>{t('dashboard.charts.usersPerAppVersion')}</ChartFooter>
    </ChartContainer>
  );
};
